import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '../store/ui'

/**
 * Bridges notification clicks into the SPA.
 *
 * A click on an already-open tab never reloads the page, so the service worker
 * posts the target URL instead and this hook routes to it. It also reads the
 * `?date=` the notification carries so the week view opens on the right day.
 */
export function useNotificationNavigation(): void {
  const navigate = useNavigate()
  const setFocusedDate = useUIStore((s) => s.setFocusedDate)

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | undefined
      if (data?.type === 'notification-click' && data.url) navigate(data.url)
    }

    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [navigate])

  // Applies on first paint (cold open from a notification) and again whenever
  // the in-app navigation above lands on a /week URL carrying a date.
  useEffect(() => {
    const applyDateParam = () => {
      if (window.location.pathname !== '/week') return
      const date = new URLSearchParams(window.location.search).get('date')
      if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) setFocusedDate(date)
    }
    applyDateParam()
    window.addEventListener('popstate', applyDateParam)
    return () => window.removeEventListener('popstate', applyDateParam)
  }, [setFocusedDate])
}
